import React, { useEffect, useState } from "react";
import { formatDateAU } from "../utils/formatDate";
import FaceMatchBadge, { faceMatchSentence } from "./FaceMatchBadge";

/**
 * Identity verification (FOXID) — one shared card for BOTH the client member
 * page and the admin member modal. Leads with the face-match result (selfie ↔
 * licence photo + verdict), then the licence images, then every extracted
 * field. Styling lives in App.css (.idv-* / .md-section) so it themes cleanly.
 *
 *   doc:       id_document metadata (has_*, face_match, face_match_status,
 *              extracted, document_type, foxid_transaction_id, created_at…)
 *   loadImage: (which) => Promise<{ dataUrl }>   — portal-specific image fetch
 */
const KNOWN_ORDER = [
  "Document_Type", "Licence_Type", "First_Name", "Middle_Name", "Last_Name",
  "Date_Of_Birth", "Gender", "Licence_Number", "Card_Number", "Licence_Expiry",
  "Front_Address", "Suburb", "State_Name", "State_Code", "Postcode",
  "Country_Name", "Place_Of_Issue",
];
const humanize = (k) => String(k).replace(/_/g, " ").replace(/\s+/g, " ").trim().replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\bId\b/g, "ID");
const looksIso = (s) => /^\d{4}-\d{2}-\d{2}(T|$)/.test(String(s));
function extractedRows(ex) {
  const rows = [];
  const push = (label, val) => {
    if (val === null || val === undefined || val === "") return;
    if (typeof val === "object") { Object.entries(val).forEach(([sk, sv]) => push(`${label} · ${humanize(sk)}`, sv)); return; }
    let d = String(val); if (looksIso(d)) d = formatDateAU(d);
    rows.push([label, d]);
  };
  const seen = new Set();
  KNOWN_ORDER.forEach((k) => { if (k in ex) { push(humanize(k), ex[k]); seen.add(k); } });
  Object.keys(ex).filter((k) => !seen.has(k)).sort().forEach((k) => push(humanize(k), ex[k]));
  return rows;
}

function Tile({ label, url, w, h }) {
  return (
    <div className="idv-tile">
      <span className="idv-tile-label">{label}</span>
      {url === undefined ? (
        <div className="idv-ph" style={{ width: w, height: h }} />
      ) : url === null ? (
        <div className="idv-ph" style={{ width: w, height: h }}>unavailable</div>
      ) : (
        <a href={url} target="_blank" rel="noreferrer" title={`Open ${label} full size`}>
          <img className="idv-img" src={url} alt={label} style={{ width: w, height: h }} />
        </a>
      )}
    </div>
  );
}

export default function IdentityVerificationCard({ doc, loadImage }) {
  const [images, setImages] = useState({});
  const [showFields, setShowFields] = useState(false);

  useEffect(() => {
    if (!doc) return;
    let alive = true;
    setImages({});
    ["selfie", "face", "front", "back", "signature"].filter((w) => doc[`has_${w}`]).forEach((which) => {
      Promise.resolve(loadImage(which))
        .then((r) => { if (alive) setImages((p) => ({ ...p, [which]: (r && r.dataUrl) || null })); })
        .catch(() => { if (alive) setImages((p) => ({ ...p, [which]: null })); });
    });
    return () => { alive = false; };
  }, [doc, loadImage]);

  if (!doc) {
    return (
      <div className="md-section">
        <Head status={null} />
        <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "0.9rem" }}>No identity document was captured for this member.</p>
      </div>
    );
  }

  const rows = extractedRows(doc.extracted || {});
  const status = doc.face_match_status;
  const fm = doc.face_match;
  const sentence = faceMatchSentence(status, fm, !!doc.has_front);
  const simColor = status === "matched" ? "var(--success)" : status === "failed" ? "var(--danger)" : "var(--text)";

  return (
    <div className="md-section">
      <Head status={status} docType={doc.document_type} capturedAt={doc.created_at} />

      {/* Face-match result up front */}
      {(doc.has_selfie || doc.has_face) ? (
        <div className="idv-panel">
          <div className="idv-tiles">
            {doc.has_selfie && <Tile label="Selfie" url={images.selfie} w={120} h={148} />}
            {doc.has_face && <Tile label="Licence photo" url={images.face} w={120} h={148} />}
          </div>
          <div className="idv-verdict">
            {fm && fm.Similarity != null && (
              <>
                <div className="idv-verdict-sim" style={{ color: simColor }}>{fm.Similarity}%</div>
                <div className="idv-verdict-thr">similarity{fm.Threshold != null ? ` · threshold ${fm.Threshold}%` : ""}</div>
              </>
            )}
            {sentence && <div className="idv-verdict-line" style={{ marginTop: fm && fm.Similarity != null ? "0.6rem" : 0 }}>{sentence}</div>}
          </div>
        </div>
      ) : (
        sentence && <p style={{ margin: "0 0 1.1rem", fontSize: "0.9rem", color: "var(--text)" }}>{sentence}</p>
      )}

      {/* Licence images */}
      {(doc.has_front || doc.has_back || doc.has_signature) && (
        <div style={{ marginBottom: rows.length ? "1.1rem" : 0 }}>
          <div className="idv-sub">Licence images</div>
          <div className="idv-tiles">
            {doc.has_front && <Tile label="Front" url={images.front} w={200} h={126} />}
            {doc.has_back && <Tile label="Back" url={images.back} w={200} h={126} />}
            {doc.has_signature && <Tile label="Signature" url={images.signature} w={180} h={84} />}
          </div>
        </div>
      )}

      {/* Extracted fields — collapsed to keep the card calm */}
      {rows.length > 0 && (
        <div>
          <button type="button" className="idv-toggle" onClick={() => setShowFields((v) => !v)}>
            {showFields ? "Hide" : "Show"} extracted details ({rows.length})
          </button>
          {showFields && (
            <div className="idv-fields">
              {rows.map(([k, v]) => (
                <div key={k}>
                  <div className="idv-tile-label">{k}</div>
                  <div style={{ fontSize: "0.88rem", wordBreak: "break-word", marginTop: "0.15rem" }}>{v}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {doc.foxid_transaction_id && (
        <div style={{ marginTop: "0.9rem", fontSize: "0.7rem", color: "var(--text-muted)" }}>
          FOXID transaction <span style={{ fontFamily: "monospace" }}>{doc.foxid_transaction_id}</span>
        </div>
      )}
    </div>
  );
}

function Head({ status, docType, capturedAt }) {
  return (
    <div className="idv-head">
      <span className="idv-title">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" />
        </svg>
        Identity verification
      </span>
      {status && <FaceMatchBadge status={status} />}
      {docType && <span className="md-pill">{docType}</span>}
      {capturedAt && <span className="idv-meta" style={{ marginLeft: "auto" }}>Captured {formatDateAU(capturedAt)}</span>}
    </div>
  );
}
